import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Logo from '../../assets/img/logo-color.png';
import { VerificacaoDocumentoViewModel, DocumentVerificationResponse } from '../../viewmodel/VerificacaoDocumento/VerificacaoDocumentoViewModel';
import BackendErrorMessage from '../../components/BackendErrorMessage';

const VerificacaoDocumento: React.FC = () => {
  const { codigo } = useParams<{ codigo?: string }>();
  const navigate = useNavigate();
  const [codigoInput, setCodigoInput] = useState(codigo || '');
  const [verificando, setVerificando] = useState(false);
  const [resultado, setResultado] = useState<DocumentVerificationResponse | null>(null);
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    if (codigo) {
      verificarDocumento(codigo);
    }
    setTimeout(() => setFadeIn(true), 100);
  }, [codigo]);

  const verificarDocumento = async (codigoVerificacao: string) => {
    setVerificando(true);
    setResultado(null);
    
    try {
      const codigoLimpo = codigoVerificacao.replace(/[-\s]/g, '').toUpperCase();
      const response = await VerificacaoDocumentoViewModel.verificarDocumento(codigoLimpo);
      setResultado(response);
    } catch (error) {
      setResultado({
        valido: false,
        mensagem: BackendErrorMessage(error) || 'Erro ao verificar documento. Tente novamente.'
      });
    } finally {
      setVerificando(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (codigoInput.trim()) {
      const codigoLimpo = codigoInput.replace(/[-\s]/g, '');
      navigate(`/verificacao/${codigoLimpo}`);
      verificarDocumento(codigoLimpo);
    }
  };

  const formatarCodigo = (codigo: string) => {
    const limpo = codigo.replace(/[-\s]/g, '');
    return limpo.match(/.{1,4}/g)?.join('-') || limpo;
  };

  const formatarData = (dataISO?: string) => {
    if (!dataISO) return '-';
    const data = new Date(dataISO);
    return data.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        maxWidth: '560px',
        width: '100%',
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        padding: '3rem',
        transform: fadeIn ? 'translateY(0)' : 'translateY(20px)',
        opacity: fadeIn ? 1 : 0,
        transition: 'all 0.6s ease-out'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#1e293b',
            margin: '0 0 0.5rem 0'
          }}>Verificação de Documento</h1>
          <p style={{
            color: '#64748b',
            fontSize: '0.875rem',
            margin: 0,
            lineHeight: '1.4'
          }}>Sistema Identifica UNICAMP</p>
        </div>

        {/* Formulário de Busca */}
        {!resultado && (
          <form onSubmit={handleSubmit} style={{ marginBottom: '2rem' }}>
            <label style={{
              display: 'block',
              color: '#475569',
              fontSize: '0.875rem',
              fontWeight: '600',
              marginBottom: '0.5rem'
            }}>
              Código de Verificação
            </label>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <input
                type="text"
                value={codigoInput}
                onChange={(e) => setCodigoInput(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX-XXXX"
                maxLength={14}
                style={{
                  flex: 1,
                  padding: '0.75rem 1rem',
                  fontSize: '1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  fontFamily: 'monospace',
                  letterSpacing: '2px',
                  textAlign: 'center'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
              <button
                type="submit"
                disabled={verificando || !codigoInput.trim()}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: verificando ? '#94a3b8' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: verificando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  if (!verificando) e.currentTarget.style.backgroundColor = '#2563eb';
                }}
                onMouseLeave={(e) => {
                  if (!verificando) e.currentTarget.style.backgroundColor = '#3b82f6';
                }}
              >
                {verificando ? 'Verificando...' : 'Verificar'}
              </button>
            </div>
            <p style={{
              color: '#64748b',
              fontSize: '0.75rem',
              marginTop: '0.5rem',
              textAlign: 'center'
            }}>
              Digite o código que aparece no rodapé do documento
            </p>
          </form>
        )}

        {/* Loading */}
        {verificando && (
          <div style={{ padding: '3rem 0', textAlign: 'center' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #e2e8f0',
              borderTop: '3px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }} />
            <p style={{ color: '#64748b', margin: 0 }}>Verificando autenticidade...</p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}

        {/* Resultado */}
        {resultado && !verificando && (
          <div style={{
            opacity: fadeIn ? 1 : 0,
            transform: fadeIn ? 'scale(1)' : 'scale(0.95)',
            transition: 'all 0.4s ease-out 0.2s'
          }}>
            {/* Ícone de Status */}
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: resultado.valido ? '#22c55e' : '#ef4444',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 auto 1.5rem',
              animation: 'bounce 0.6s ease-out'
            }}>
              {resultado.valido ? '✓' : '✕'}
            </div>

            {/* Mensagem Principal */}
            <div style={{
              padding: '1.5rem',
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              marginBottom: '1.5rem',
              border: `2px solid ${resultado.valido ? '#22c55e' : '#ef4444'}20`,
              textAlign: 'center'
            }}>
              <p style={{
                color: '#1e293b',
                fontSize: '1.125rem',
                fontWeight: '600',
                margin: 0
              }}>
                {resultado.mensagem}
              </p>
            </div>

            {/* Detalhes do Documento */}
            {resultado.valido && (
              <div style={{
                backgroundColor: '#f8fafc',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                <h3 style={{
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#475569',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '1rem'
                }}>
                  Detalhes do Documento
                </h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {resultado.codigoVerificacao && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Código:</span>
                      <span style={{
                        color: '#1e293b',
                        fontWeight: '600',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        letterSpacing: '1px'
                      }}>
                        {formatarCodigo(resultado.codigoVerificacao)}
                      </span>
                    </div>
                  )}
                  
                  {resultado.tipoDocumentoDescricao && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Tipo:</span>
                      <span style={{ color: '#1e293b', fontWeight: '600', fontSize: '0.875rem' }}>
                        {resultado.tipoDocumentoDescricao}
                      </span>
                    </div>
                  )}
                  
                  {resultado.dataGeracao && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Data de Geração:</span>
                      <span style={{ color: '#1e293b', fontWeight: '600', fontSize: '0.875rem' }}>
                        {formatarData(resultado.dataGeracao)}
                      </span>
                    </div>
                  )}
                  
                  {resultado.candidatoNome && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Candidato:</span>
                      <span style={{ color: '#1e293b', fontWeight: '600', fontSize: '0.875rem' }}>
                        {resultado.candidatoNome}
                      </span>
                    </div>
                  )}
                  
                  {resultado.processoIdentificador && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Processo:</span>
                      <span style={{ color: '#1e293b', fontWeight: '600', fontSize: '0.875rem' }}>
                        {resultado.processoIdentificador}
                      </span>
                    </div>
                  )}
                  
                  {resultado.unidadeNome && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Unidade:</span>
                      <span style={{ color: '#1e293b', fontWeight: '600', fontSize: '0.875rem' }}>
                        {resultado.unidadeNome}
                      </span>
                    </div>
                  )}
                  
                  {resultado.totalVerificacoes !== undefined && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Verificações:</span>
                      <span style={{ color: '#1e293b', fontWeight: '600', fontSize: '0.875rem' }}>
                        {resultado.totalVerificacoes}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Botão Nova Verificação */}
            <button
              onClick={() => {
                setResultado(null);
                setCodigoInput('');
                navigate('/verificacao');
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: '#f1f5f9',
                color: '#475569',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                marginBottom: '1.5rem'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e2e8f0'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
            >
              Nova Verificação
            </button>

            {/* Logo */}
            <div style={{ textAlign: 'center' }}>
              <img 
                src={Logo} 
                alt="Logo Identifica" 
                style={{
                  maxWidth: '120px',
                  height: 'auto',
                  opacity: 0.8
                }}
              />
            </div>

            <style>{`
              @keyframes bounce {
                0%, 20%, 53%, 80%, 100% {
                  transform: translate3d(0,0,0);
                }
                40%, 43% {
                  transform: translate3d(0, -8px, 0);
                }
                70% {
                  transform: translate3d(0, -4px, 0);
                }
                90% {
                  transform: translate3d(0, -2px, 0);
                }
              }
            `}</style>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerificacaoDocumento;
